//
// Copyright 2025 DXOS.org
//
import { BaseError } from '@dxos/errors';
import { EdgeHttpErrorCodec, ErrorCodec } from './edge.js';
/**
 * Error thrown when a call to the Edge service fails.
 * There 3 possible sources of failure:
 * 1. EdgeFailure (JSON body with { success: false }) -> Processing the request failed and was gracefully handled by EDGE service.
 * 2. Http failure (non-ok code) -> The HTTP request failed (e.g. timeout, network error).
 *                               -> Unhandled exception on EDGE side, EDGE would provide serialized error in the response body.
 * 3. Processing failure -> Unhandled exception on client side while processing the response.
 */
export class EdgeCallFailedError extends BaseError.extend('EdgeCallFailedError', 'EDGE call failed.') {
    static fromUnsuccessfulResponse(response, body) {
        const error = new EdgeCallFailedError({
            message: body.message,
            data: body.data,
            isRetryable: body.data == null && response.headers.has('Retry-After'),
            retryAfterMs: getRetryAfterMillis(response),
            status: response.status,
            cause: body.error ? ErrorCodec.decode(body.error) : undefined,
        });
        return error;
    }
    static async fromHttpFailure(response) {
        return new EdgeCallFailedError({
            message: `HTTP code ${response.status}: ${response.statusText}.`,
            isRetryable: isRetryableCode(response.status),
            retryAfterMs: getRetryAfterMillis(response),
            status: response.status,
            cause: await EdgeHttpErrorCodec.decode(response),
        });
    }
    static fromProcessingFailureCause(cause) {
        return new EdgeCallFailedError({
            message: 'Error processing request.',
            isRetryable: true,
            cause,
        });
    }
    data;
    isRetryable;
    retryAfterMs;
    /** HTTP status of the response, absent when no response arrived (a network or client-side failure). */
    status;
    constructor(args) {
        super({ message: args.message, cause: args.cause });
        this.data = args.data;
        this.retryAfterMs = args.retryAfterMs;
        this.status = args.status;
        this.isRetryable = Boolean(args.isRetryable);
    }
}
export class EdgeAuthChallengeError extends EdgeCallFailedError {
    challenge;
    constructor(challenge, data) {
        super({ message: 'Auth challenge.', data, isRetryable: false });
        this.challenge = challenge;
    }
}
/**
 * EDGE refused this client SDK as older than the oldest it serves (`data.type` `EDGE_CLIENT_TOO_OLD`). Not
 * retryable: every retry carries the same version, and only updating the app clears it.
 */
export class EdgeClientTooOldError extends EdgeCallFailedError {
    constructor(body, status) {
        super({ message: body.message, data: body.data, status, isRetryable: false });
    }
}
const getRetryAfterMillis = (response) => {
    const retryAfter = Number(response.headers.get('Retry-After'));
    return Number.isNaN(retryAfter) || retryAfter === 0 ? undefined : retryAfter * 1000;
};
const isRetryableCode = (status) => {
    if (status === 501) {
        // Not Implemented
        return false;
    }
    return !(status >= 400 && status < 500);
};
//# sourceMappingURL=errors.js.map